/**
 * File Exporter Utility
 * Export translated text as TXT or DOCX (preserving original formatting).
 * 
 * DOCX Export - Co che 100% chinh xac:
 *   1. parseDocxDirect (fileParser) parse XML truc tiep -> sourceText + paragraphMap
 *   2. sourceText -> getLineSegments -> segments
 *   3. AI dich -> translatedSegments
 *   4. buildTranslatedText: dung VI TRI indexOf -> translatedFullText (100%)
 *   5. Split translatedFullText thanh lines
 *   6. paragraphMap[i] -> translatedLines[i] (CUNG PARSER, 1:1)
 *   7. Replace <w:t> content trong XML theo mapping -> DOCX output
 *   8. mc:Fallback: GIU NGUYEN (khong xoa, khong sua) -> Word mo duoc
 */

import JSZip from 'jszip';
import { getLineSegments } from './textUtils';
import type { DocxParagraphMapping, PptxSlideMapping } from './fileParser';
import { regenerateParagraphMap } from './fileParser';

// ============================================
// GUARD HELPER - Check originalFile is a real ArrayBuffer
// (After page reload, originalFile may be {} due to JSON.stringify stripping it)
// ============================================
function isValidArrayBuffer(value: any): value is ArrayBuffer {
    return value instanceof ArrayBuffer && value.byteLength > 0;
}

// ============================================
// HELPERS
// ============================================

function downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

function escapeXml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

// ============================================
// BOLD PRESERVATION HELPERS
// ============================================

interface BoldSegment {
    text: string;
    bold: boolean;
}

/**
 * Parse text co ** markers thanh segments.
 * "Hello **bold** world" → [{text:"Hello ",bold:false},{text:"bold",bold:true},{text:" world",bold:false}]
 */
function parseMarkdownBold(text: string): BoldSegment[] {
    if (!text.includes('**')) {
        return [{ text, bold: false }];
    }
    const segments: BoldSegment[] = [];
    const parts = text.split('**');
    for (let i = 0; i < parts.length; i++) {
        if (parts[i] === '') continue;
        segments.push({ text: parts[i], bold: i % 2 === 1 });
    }
    return segments;
}

/**
 * Extract <w:rPr>...</w:rPr> tu run XML, loai bo <w:b/> va <w:bCs/>.
 * Tra ve full tag (e.g. "<w:rPr><w:rFonts .../><w:color .../></w:rPr>") hoac ''.
 */
function extractBaseRprFull(runXml: string): string {
    const m = runXml.match(/<w:rPr>[\s\S]*?<\/w:rPr>/);
    if (!m) return '';
    let rPr = m[0];
    rPr = rPr.replace(/<w:b[^>]*\/>\s*/g, '');
    rPr = rPr.replace(/<w:b[^>]*>[\s\S]*?<\/w:b>\s*/g, '');
    rPr = rPr.replace(/<w:bCs[^>]*\/>\s*/g, '');
    return rPr;
}

/**
 * Insert <w:b/> vao dung vi tri trong <w:rPr>.
 * OOXML yeu cau thu tu: rStyle, rFonts, b, bCs, i, iCs, ...
 */
function insertBoldIntoRpr(rPrXml: string): string {
    if (!rPrXml) return '<w:rPr><w:b/></w:rPr>';
    if (rPrXml.includes('<w:b/>') || rPrXml.includes('<w:b ')) return rPrXml;
    // Insert sau <w:rFonts.../>
    const rf = rPrXml.match(/<w:rFonts[^>]*\/>/);
    if (rf) {
        const p = rf.index! + rf[0].length;
        return rPrXml.substring(0, p) + '<w:b/>' + rPrXml.substring(p);
    }
    // Insert sau <w:rStyle.../>
    const rs = rPrXml.match(/<w:rStyle[^>]*\/>/);
    if (rs) {
        const p = rs.index! + rs[0].length;
        return rPrXml.substring(0, p) + '<w:b/>' + rPrXml.substring(p);
    }
    return rPrXml.replace('<w:rPr>', '<w:rPr><w:b/>');
}

/**
 * Build 1 <w:r> XML tu text, base rPr, va bold flag.
 */
function buildBoldRunXml(text: string, baseRprFull: string, bold: boolean): string {
    let rPr = baseRprFull;
    if (bold) {
        rPr = rPr ? insertBoldIntoRpr(rPr) : '<w:rPr><w:b/></w:rPr>';
    }
    if (rPr === '<w:rPr></w:rPr>') rPr = '';
    const needPreserve = text.startsWith(' ') || text.endsWith(' ');
    const spaceAttr = needPreserve ? ' xml:space="preserve"' : '';
    return `<w:r>${rPr}<w:t${spaceAttr}>${escapeXml(text)}</w:t></w:r>`;
}

// ============================================
// EXPORT TXT
// ============================================

export function exportTxt(segments: string[], fileName: string): void {
    const text = segments.join('\n');
    const blob = new Blob(['\ufeff' + text], { type: 'text/plain;charset=utf-8' });
    const baseName = fileName.replace(/\.[^.]+$/, '');
    downloadBlob(blob, `${baseName}_translated.txt`);
}

// ============================================
// BUOC 1: Ghep nguoc segments -> translatedFullText (100% chinh xac bang vi tri)
// ============================================

/**
 * Map translated segments nguoc vao paragraphs (reverse flow).
 * 
 * Logic:
 * 1. Tinh offset cua moi paragraph trong sourceText
 * 2. Voi moi segment, tim paragraph chua no
 * 3. Gom translated segments theo paragraph
 * 4. Giu nguyen khoang trang goc giua cac segments
 */
function mapSegmentsToParagraphs(
    sourceText: string,
    activeParas: DocxParagraphMapping[],
    translatedSegments: string[]
): Map<number, string> {
    const segments = getLineSegments(sourceText);
    
    // Buoc 1: Tinh offset cua moi paragraph trong sourceText
    // sourceText = textLines.join('\n'), nen moi textLine = 1 paragraph
    const paraOffsets: Array<{ start: number; end: number }> = [];
    let offset = 0;
    for (const para of activeParas) {
        paraOffsets.push({ 
            start: offset, 
            end: offset + para.text.length 
        });
        offset += para.text.length + 1; // +1 cho '\n'
    }

    // DEBUG: Log first 10 paragraphs and segments for diagnosis
    console.log(`[ExportDocx][MapDebug] sourceText length: ${sourceText.length}, segments: ${segments.length}, activeParas: ${activeParas.length}, translatedSegments: ${translatedSegments.length}`);
    for (let d = 0; d < Math.min(10, activeParas.length); d++) {
        console.log(`[ExportDocx][MapDebug] PARA[${d}] offset=${paraOffsets[d].start}-${paraOffsets[d].end} text="${activeParas[d].text.substring(0, 60)}" xmlStart=${activeParas[d].xmlStart}`);
    }
    for (let d = 0; d < Math.min(10, segments.length); d++) {
        const t = translatedSegments[d];
        console.log(`[ExportDocx][MapDebug] SEG[${d}] start=${segments[d].start} end=${segments[d].end} text="${segments[d].text.substring(0, 60)}" translated="${(t || '').substring(0, 60)}"`);
    }
    
    // Buoc 2 & 3: Map segment -> paragraph va gom text
    const paraTranslated = new Map<number, { text: string; lastEnd: number }>();
    
    for (let j = 0; j < segments.length; j++) {
        const seg = segments[j];
        const translated = translatedSegments[j];
        if (translated === undefined || translated === '') continue;
        
        // Tim vi tri thuc te cua segment trong sourceText (fallback neu start khong dung)
        // getLineSegments co the tra ve start tuong doi, nen ta tim lai cho chac
        const actualStart = sourceText.indexOf(seg.text, j === 0 ? 0 : Math.max(0, segments[j-1].end));
        const segStart = actualStart !== -1 ? actualStart : seg.start;
        const segEnd = segStart + seg.text.length;
        
        // Tim paragraph chua segment nay
        const paraIdx = paraOffsets.findIndex(
            p => segStart >= p.start && segStart < p.end
        );
        
        if (paraIdx === -1) {
            console.warn(`[ExportDocx] Khong tim thay paragraph cho segment: "${seg.text.substring(0, 30)}"`);
            continue;
        }
        
        const para = paraOffsets[paraIdx];
        
        if (paraTranslated.has(paraIdx)) {
            const current = paraTranslated.get(paraIdx)!;
            // Giu nguyen khoang trang goc giua segment truoc va segment nay
            const gap = sourceText.substring(current.lastEnd, segStart);
            paraTranslated.set(paraIdx, {
                text: current.text + gap + translated,
                lastEnd: segEnd
            });
        } else {
            // Segment dau tien cua paragraph
            // Giu nguyen prefix cua paragraph (tu dau paragraph den start cua segment)
            const leadingGap = sourceText.substring(para.start, segStart);
            paraTranslated.set(paraIdx, {
                text: leadingGap + translated,
                lastEnd: segEnd
            });
        }
    }
    
    // Buoc 4: Format ket qua cuoi cung va them suffix (tu cuoi segment cuoi cung den cuoi paragraph)
    const result = new Map<number, string>();
    for (const [paraIdx, data] of paraTranslated.entries()) {
        const para = paraOffsets[paraIdx];
        const trailingGap = sourceText.substring(data.lastEnd, para.end);
        result.set(paraIdx, data.text + trailingGap);
    }
    
    return result;
}

// ============================================
// BUOC 2: Replace text trong 1 paragraph XML
// ============================================

/**
 * Thay doi noi dung text cua 1 <w:p> paragraph.
 *
 * Ho tro ** bold markers:
 * - Neu newText co ** markers → tao multi-run XML (bold/non-bold)
 * - Neu newText KHONG co ** → fallback logic cu (tat ca vao 1 run)
 *
 * Giu nguyen:
 * - Nested elements (mc:AlternateContent, mc:Fallback, nested w:p, etc.)
 * - Drawing/pict/object runs
 * - Content GIUA cac runs (proofErr, comment, bookmark)
 */
function replaceParagraphText(paragraph: string, newText: string): string {
    // Unescape \*\* that AI may return (Gemini sometimes escapes ** as \*\*)
    const cleanedText = newText.replace(/\\\*\\\*/g, '**');

    const directRunRanges = findDirectRunRanges(paragraph);

    if (directRunRanges.length === 0) {
        return paragraph;
    }

    // Separate text-only runs from non-text runs.
    // Skip: drawings, pictures, objects, page breaks (<w:br>), deleted text (<w:delText>)
    // Key fix: skip runs that have NO <w:t> — these are break-only or deleted-text-only runs
    const textRunRanges = directRunRanges.filter(r => {
        const xml = paragraph.substring(r.start, r.end);
        if (xml.includes('<w:drawing') || xml.includes('<w:pict') || xml.includes('<w:object')) return false;
        if (!xml.includes('<w:t')) return false; // skip runs without actual text (page breaks, w:delText)
        return true;
    });

    if (textRunRanges.length === 0) {
        return paragraph;
    }

    // Check for bold markers
    const boldSegments = parseMarkdownBold(cleanedText);
    const hasBold = boldSegments.some(s => s.bold);

    if (hasBold) {
        // === BOLD-AWARE: tao multi-run XML ===
        const baseRpr = extractBaseRprFull(paragraph.substring(textRunRanges[0].start, textRunRanges[0].end));
        let newRunsXml = '';
        for (const seg of boldSegments) {
            newRunsXml += buildBoldRunXml(seg.text, baseRpr, seg.bold);
        }
        // Delete text runs from end to start, replace first with new runs
        let result = paragraph;
        for (let i = textRunRanges.length - 1; i >= 0; i--) {
            if (i === 0) {
                result = result.substring(0, textRunRanges[i].start) + newRunsXml + result.substring(textRunRanges[i].end);
            } else {
                result = result.substring(0, textRunRanges[i].start) + result.substring(textRunRanges[i].end);
            }
        }
        return result;
    }

    // === LEGACY: no bold markers, strip any stray ** and put all text in first run ===
    const cleanText = cleanedText.replace(/\*\*/g, '');
    let result = paragraph;
    for (let i = textRunRanges.length - 1; i >= 0; i--) {
        const run = textRunRanges[i];
        const runXml = result.substring(run.start, run.end);

        if (i === 0) {
            // First text run: replace <w:t> content
            let firstWt = true;
            const newRunXml = runXml.replace(/<w:t( [^>]*)?>[\s\S]*?<\/w:t>/g, (_m: string, attrs: string) => {
                const safeAttrs = attrs || '';
                if (firstWt) {
                    firstWt = false;
                    let newAttrs = safeAttrs;
                    if (!safeAttrs.includes('xml:space') && (cleanText.startsWith(' ') || cleanText.endsWith(' '))) {
                        newAttrs = ` xml:space="preserve"${safeAttrs}`;
                    }
                    return `<w:t${newAttrs}>${escapeXml(cleanText)}</w:t>`;
                }
                return `<w:t${safeAttrs}></w:t>`;
            });
            result = result.substring(0, run.start) + newRunXml + result.substring(run.end);
        } else {
            // Other text runs: delete
            result = result.substring(0, run.start) + result.substring(run.end);
        }
    }

    // Handle drawing runs separately (replace text inside them)
    const drawingRuns = directRunRanges.filter(r => {
        const xml = paragraph.substring(r.start, r.end);
        return xml.includes('<w:drawing') || xml.includes('<w:pict') || xml.includes('<w:object');
    });
    for (const run of drawingRuns) {
        const runXml = result.substring(run.start, run.end);
        let firstWt = true;
        const newRunXml = runXml.replace(/<w:t( [^>]*)?>[\s\S]*?<\/w:t>/g, (_m: string, attrs: string) => {
            const safeAttrs = attrs || '';
            if (firstWt) {
                firstWt = false;
                let newAttrs = safeAttrs;
                if (!safeAttrs.includes('xml:space') && (cleanText.startsWith(' ') || cleanText.endsWith(' '))) {
                    newAttrs = ` xml:space="preserve"${safeAttrs}`;
                }
                return `<w:t${newAttrs}>${escapeXml(cleanText)}</w:t>`;
            }
            return `<w:t${safeAttrs}></w:t>`;
        });
        result = result.substring(0, run.start) + newRunXml + result.substring(run.end);
    }

    return result;
}

/**
 * Tim tat ca direct <w:r>...</w:r> ranges trong paragraph XML.
 * Skip nested elements (mc:AlternateContent, mc:Fallback, nested w:p, etc.)
 */
function findDirectRunRanges(paragraph: string): Array<{ start: number; end: number }> {
    // Skip: nested <w:p>, mc:*, wps:txbx, w:txbxContent, w:del (deleted track changes content)
    // w:del: runs inside are deleted content → keep as-is, do NOT replace
    // w:ins: runs inside are inserted content → SHOULD be replaced (not skipped)
    const nestingTags = ['<w:p ', '<w:p>', '<mc:AlternateContent', '<mc:Fallback', '<wps:txbx', '<w:txbxContent', '<w:del '];
    const closingMap: Record<string, string> = {
        '<w:p ': '</w:p>',
        '<w:p>': '</w:p>',
        '<mc:AlternateContent': '</mc:AlternateContent>',
        '<mc:Fallback': '</mc:Fallback>',
        '<wps:txbx': '</wps:txbx>',
        '<w:txbxContent': '</w:txbxContent>',
        '<w:del ': '</w:del>',
    };

    const nestedRanges: Array<{ start: number; end: number }> = [];
    for (const openTag of nestingTags) {
        let searchPos = 0;
        const isWp = openTag.startsWith('<w:p');
        let skipFirst = isWp;
        while (searchPos < paragraph.length) {
            const idx = paragraph.indexOf(openTag, searchPos);
            if (idx === -1) break;
            if (skipFirst) { skipFirst = false; searchPos = idx + openTag.length; continue; }
            const closeTag = closingMap[openTag];
            const closeIdx = paragraph.indexOf(closeTag, idx + openTag.length);
            if (closeIdx !== -1) nestedRanges.push({ start: idx, end: closeIdx + closeTag.length });
            searchPos = (closeIdx !== -1) ? closeIdx + closeTag.length : idx + openTag.length;
        }
    }

    const directRunRanges: Array<{ start: number; end: number }> = [];
    let searchPos = 0;
    while (searchPos < paragraph.length) {
        const wrIdx1 = paragraph.indexOf('<w:r ', searchPos);
        const wrIdx2 = paragraph.indexOf('<w:r>', searchPos);
        let wrIdx = -1;
        if (wrIdx1 === -1 && wrIdx2 === -1) break;
        else if (wrIdx1 === -1) wrIdx = wrIdx2;
        else if (wrIdx2 === -1) wrIdx = wrIdx1;
        else wrIdx = Math.min(wrIdx1, wrIdx2);

        if (nestedRanges.some(r => wrIdx >= r.start && wrIdx < r.end)) {
            searchPos = wrIdx + 5; continue;
        }

        let depth = 1;
        let pos = paragraph.indexOf('>', wrIdx) + 1;
        while (pos < paragraph.length && depth > 0) {
            const nextOpen1 = paragraph.indexOf('<w:r ', pos);
            const nextOpen2 = paragraph.indexOf('<w:r>', pos);
            const nextClose = paragraph.indexOf('</w:r>', pos);
            if (nextClose === -1) break;
            let nextOpen = -1;
            if (nextOpen1 === -1 && nextOpen2 === -1) nextOpen = -1;
            else if (nextOpen1 === -1) nextOpen = nextOpen2;
            else if (nextOpen2 === -1) nextOpen = nextOpen1;
            else nextOpen = Math.min(nextOpen1, nextOpen2);
            if (nextOpen !== -1 && nextOpen < nextClose) {
                depth++; pos = paragraph.indexOf('>', nextOpen) + 1;
            } else {
                depth--;
                if (depth === 0) directRunRanges.push({ start: wrIdx, end: nextClose + '</w:r>'.length });
                pos = nextClose + '</w:r>'.length;
            }
        }
        searchPos = directRunRanges.length > 0 ? directRunRanges[directRunRanges.length - 1].end : wrIdx + 5;
    }

    return directRunRanges;
}

// ============================================
// EXPORT DOCX - ParagraphMap-based (100% chinh xac)
// ============================================

export async function exportDocx(
    originalFile: ArrayBuffer,
    sourceSegments: string[],
    translatedSegments: string[],
    fileName: string,
    sourceText?: string,
    paragraphMap?: DocxParagraphMapping[]
): Promise<void> {
    if (!sourceText) {
        console.error('[ExportDocx] Can sourceText de export DOCX chinh xac');
        return;
    }

    // Guard: originalFile phai la ArrayBuffer hop le.
    if (!isValidArrayBuffer(originalFile)) {
        alert('File DOCX goc khong con trong bo nho (do reload trang).\nVui long upload lai file DOCX de export.');
        console.error('[ExportDocx] originalFile la', originalFile, '- khong phai ArrayBuffer hop le');
        return;
    }

    // 1. Mo file DOCX goc
    const zip = await JSZip.loadAsync(originalFile);
    const docXmlFile = zip.file('word/document.xml');
    if (!docXmlFile) throw new Error('File DOCX khong hop le: thieu word/document.xml');
    const docXml = await docXmlFile.async('string');

    // 2. Lay cac active paragraphs (non-fallback) tu paragraphMap
    let activeParas = paragraphMap
        ? paragraphMap.filter(p => !p.isFallback)
        : [];

    if (activeParas.length === 0) {
        // Fallback: try to regenerate from originalFile
        console.warn('[ExportDocx] ParagraphMap missing. Attempting to regenerate from DOCX...');
        try {
            const regenerated = await regenerateParagraphMap(originalFile);
            const regenActive = regenerated.filter(p => !p.isFallback);
            if (regenActive.length > 0) {
                console.log(`[ExportDocx] Regenerated ${regenActive.length} active paragraphs successfully`);
                paragraphMap = regenerated;
                activeParas = regenActive;
            } else {
                console.error('[ExportDocx] Regeneration returned 0 active paragraphs');
                alert('Khong the tao lai paragraphMap tu file DOCX goc. Vui long upload lai file DOCX.');
                return;
            }
        } catch (regenErr) {
            console.error('[ExportDocx] Regeneration failed:', regenErr);
            alert('Khong the tao lai paragraphMap tu file DOCX goc. Vui long upload lai file DOCX.');
            return;
        }
    }

    console.log(`[ExportDocx] ParagraphMap: ${activeParas.length} active, ${(paragraphMap || []).filter(p => p.isFallback).length} fallback`);

    // 3. Xay mapping segments -> paragraphs (reverse flow)
    const paraTranslatedMap = mapSegmentsToParagraphs(
        sourceText, activeParas, translatedSegments
    );

    console.log(`[ExportDocx] Mapped segments to paragraphs: ${paraTranslatedMap.size}/${activeParas.length} active paras`);

    // 5. PARAGRAPH-MAP BASED REPLACEMENT
    // Replace tu cuoi len dau de khong lam lech vi tri
    let modifiedXml = docXml;
    let replacedCount = 0;
    const debugEntries: { index: number; srcLine: string; paraText: string; translated: string; status: string }[] = [];

    // Sort theo xmlStart giam dan de replace tu cuoi, include chi so de map lai lay tu paraTranslatedMap
    const parasToReplace = activeParas.map((p, i) => ({
        ...p,
        sourceLineIdx: i,
    }));

    // Loai bo nested paragraphs (xmlStart/xmlEnd nam TRONG 1 paragraph khac)
    // Nested paragrphs xay ra khi co text boxes, tables ben trong mc:AlternateContent
    const nonNestedParas = parasToReplace.filter((para, _idx, arr) => {
        // Kiem tra xem co paragraph nao BAO QUANH para nay khong
        const isNested = arr.some(other =>
            other !== para &&
            other.xmlStart < para.xmlStart &&
            other.xmlEnd > para.xmlEnd
        );
        if (isNested) {
            console.log(`[ExportDocx] SKIP nested para #${para.sourceLineIdx}: "${para.text.substring(0, 40)}..." (inside another <w:p>)`);
        }
        return !isNested;
    });

    console.log(`[ExportDocx] NonNested: ${nonNestedParas.length}/${parasToReplace.length} paragraphs`);

    // Sort giam dan theo xmlStart de xu ly an toan khi thay doi do dai
    nonNestedParas.sort((a, b) => b.xmlStart - a.xmlStart);

    let prevUndefinedCount = (modifiedXml.match(/undefined/g) || []).length;
    
    for (const para of nonNestedParas) {
        // Extract paragraph XML tu modifiedXml
        const paraXml = modifiedXml.substring(para.xmlStart, para.xmlEnd);

        // Safety check: neu xmlStart/xmlEnd sai -> paraXml co the rong hoac sai
        if (!paraXml || !paraXml.startsWith('<w:p')) {
            console.warn(`[ExportDocx] SKIP para #${para.sourceLineIdx}: paraXml invalid at ${para.xmlStart}-${para.xmlEnd} (got: "${(paraXml || '').substring(0, 40)}")`);
            continue;
        }

        // Lay text da gom cho paragraph nay (dung mapping moi tinh bang sourceText vi tri)
        const translatedText = paraTranslatedMap.get(para.sourceLineIdx);
        
        // DEBUG: Log heading paragraph structure for first 3
        if (para.sourceLineIdx < 3) {
            const hasDrawing = paraXml.includes('<w:drawing');
            const hasMcAlt = paraXml.includes('<mc:AlternateContent');
            const hasMcFallback = paraXml.includes('<mc:Fallback');
            const hasWt = paraXml.includes('<w:t');
            const runCount = (paraXml.match(/<w:r[ >]/g) || []).length;
            console.log(`[ExportDocx][HeadingDebug] PARA[${para.sourceLineIdx}] xmlLen=${paraXml.length} hasDrawing=${hasDrawing} hasMcAlt=${hasMcAlt} hasMcFallback=${hasMcFallback} hasWt=${hasWt} runCount=${runCount}`);
            console.log(`[ExportDocx][HeadingDebug] PARA[${para.sourceLineIdx}] first200="${paraXml.substring(0, 200)}"`);
            console.log(`[ExportDocx][HeadingDebug] PARA[${para.sourceLineIdx}] translatedText="${(translatedText || '').substring(0, 80)}"`);
        }

        let newParaXml: string;
        
        if (translatedText === undefined) {
             // Khong co text dich nao cho paragraph, giu nguyen
             newParaXml = paraXml;
        } else {
             newParaXml = replaceParagraphText(paraXml, translatedText);
             replacedCount++;
        }

        // Safety: dam bao khong co undefined
        if (newParaXml === undefined || newParaXml === null) {
            console.error(`[ExportDocx] ERROR: newParaXml is ${newParaXml} for para #${para.sourceLineIdx}`);
            newParaXml = paraXml; // Fallback: giu nguyen
        }

        // DEBUG: Check if newParaXml itself contains undefined
        const newParaUndefined = (newParaXml.match(/undefined/g) || []).length;
        const paraUndefined = (paraXml.match(/undefined/g) || []).length;
        if (newParaUndefined > paraUndefined) {
            console.error(`[ExportDocx] UNDEFINED INJECTED by replaceParagraphText for para #${para.sourceLineIdx}!`);
            console.error(`  paraXml undefined count: ${paraUndefined}, newParaXml undefined count: ${newParaUndefined}`);
            console.error(`  text: "${para.text.substring(0, 60)}"`);
            console.error(`  translated: "${(translatedText || '').substring(0, 60)}"`);
            console.error(`  paraXml length: ${paraXml.length}, contains mc:Fallback: ${paraXml.includes('mc:Fallback')}`);
            // Find first undefined
            const undIdx = newParaXml.indexOf('undefined');
            if (undIdx !== -1) {
                console.error(`  First undefined at pos ${undIdx}: ...${newParaXml.substring(Math.max(0, undIdx-50), undIdx)}[UNDEFINED]${newParaXml.substring(undIdx+9, undIdx+50)}...`);
            }
        }

        // Replace trong modifiedXml tai DUNG VI TRI
        const before = modifiedXml.substring(0, para.xmlStart);
        const after = modifiedXml.substring(para.xmlStart + paraXml.length);
        modifiedXml = before + newParaXml + after;

        // DEBUG: Check if modifiedXml gained undefined
        const currentUndefinedCount = (modifiedXml.match(/undefined/g) || []).length;
        if (currentUndefinedCount > prevUndefinedCount) {
            console.error(`[ExportDocx] UNDEFINED ADDED to modifiedXml after para #${para.sourceLineIdx}! Count: ${prevUndefinedCount} -> ${currentUndefinedCount}`);
        }
        prevUndefinedCount = currentUndefinedCount;

        debugEntries.push({
            index: para.sourceLineIdx,
            srcLine: para.text.trim().substring(0, 60), // Ban truoc dung srcLine text nay, hien tai src mapping
            paraText: para.text.trim().substring(0, 60),
            translated: (translatedText || '').substring(0, 60),
            status: translatedText ? 'OK' : 'SKIP'
        });
    }

    // Sort debug entries theo index de hien thi dung thu tu
    debugEntries.sort((a, b) => a.index - b.index);

    console.log(`[ExportDocx] Replaced ${replacedCount}/${activeParas.length} paragraphs`);

    // === FINAL DIAGNOSTIC: Xac dinh chinh xac loi o dau ===
    const origUndefined = (docXml.match(/undefined/g) || []).length;
    const finalUndefined = (modifiedXml.match(/undefined/g) || []).length;
    console.log(`[ExportDocx] DIAGNOSTIC: XML goc: ${docXml.length} chars, ${origUndefined} undefined`);
    console.log(`[ExportDocx] DIAGNOSTIC: XML sau replace: ${modifiedXml.length} chars, ${finalUndefined} undefined`);
    if (finalUndefined > origUndefined) {
        console.error(`[ExportDocx] BUG DETECTED: ${finalUndefined - origUndefined} "undefined" strings were INJECTED during replacement!`);
        // Tim vi tri dau tien cua undefined trong modifiedXml khong co trong docXml
        const idx = modifiedXml.indexOf('undefined');
        if (idx !== -1) {
            console.error(`[ExportDocx] First undefined at char ${idx}: ...${modifiedXml.substring(Math.max(0, idx-60), idx)}[UNDEFINED HERE]${modifiedXml.substring(idx+9, idx+60)}...`);
        }
    } else {
        console.log(`[ExportDocx] OK: No undefined injected`);
    }

    // Luu debug info cho nut Debug
    (window as any).__exportDocxDebug = {
        paragraphMapTexts: activeParas.map(p => p.text.trim()),
        debugEntries,
        modifiedXml, // Luu de diagnostic export dung
        stats: {
            paragraphMapCount: activeParas.length,
            fallbackCount: (paragraphMap || []).filter(p => p.isFallback).length,
            replacedCount,
        }
    };

    // 6. Replace chart labels (word/charts/chart*.xml)
    const docxChartFiles = Object.keys(zip.files)
        .filter(name => /^word\/charts\/chart\d+\.xml$/.test(name));

    if (docxChartFiles.length > 0) {
        // Build translation map from sourceText lines -> translatedSegments
        const srcLines = (sourceText || '').replace(/\r\n/g, '\n').split(/\n+/).filter(l => l.trim());
        const chartTransMap = new Map<string, string>();
        for (let i = 0; i < srcLines.length && i < translatedSegments.length; i++) {
            const src = srcLines[i].trim();
            const trans = translatedSegments[i];
            if (src && trans && trans.trim() && isNaN(Number(src))) {
                chartTransMap.set(src, trans);
            }
        }

        let chartReplaced = 0;
        for (const chartPath of docxChartFiles) {
            const chartFile = zip.file(chartPath);
            if (!chartFile) continue;

            let chartXml = await chartFile.async('string');
            let changed = false;

            for (const [src, trans] of chartTransMap.entries()) {
                const escaped = src.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                // Replace in <c:v> tags
                const cvRe = new RegExp(`(<c:v>)\\s*${escaped}\\s*(</c:v>)`, 'g');
                const before1 = chartXml;
                chartXml = chartXml.replace(cvRe, `$1${escapeXml(trans)}$2`);
                if (chartXml !== before1) changed = true;

                // Replace in <a:t> tags (chart titles)
                const atRe = new RegExp(`(<a:t(?:\\s[^>]*)?>)\\s*${escaped}\\s*(</a:t>)`, 'g');
                const before2 = chartXml;
                chartXml = chartXml.replace(atRe, `$1${escapeXml(trans)}$2`);
                if (chartXml !== before2) changed = true;
            }

            if (changed) {
                zip.file(chartPath, chartXml);
                chartReplaced++;
            }
        }
        if (chartReplaced > 0) {
            console.log(`[ExportDocx] Replaced charts in ${chartReplaced}/${docxChartFiles.length} chart files`);
        }
    }

    // 7. VALIDATION: kiem tra khong mat paragraph sau replace
    const countNonEmptyParas = (xml: string): number => {
        let count = 0;
        const wtRe = /<w:p[ >][\s\S]*?<\/w:p>/g;
        let wm;
        while ((wm = wtRe.exec(xml)) !== null) {
            if (/<w:t[^>]*>[^<]+<\/w:t>/.test(wm[0])) count++;
        }
        return count;
    };
    const origNonEmpty = countNonEmptyParas(docXml);
    const outNonEmpty = countNonEmptyParas(modifiedXml);
    const lostParas = origNonEmpty - outNonEmpty;

    if (lostParas > 0) {
        console.warn(`[ExportDocx] ⚠️ VALIDATION: ${lostParas} paragraph(s) lost text after translation (orig=${origNonEmpty}, out=${outNonEmpty})`);
    } else {
        console.log(`[ExportDocx] ✅ VALIDATION: No paragraphs lost (orig=${origNonEmpty}, out=${outNonEmpty})`);
    }

    // 8. Luu va download
    zip.file('word/document.xml', modifiedXml);
    const blob = await zip.generateAsync({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        compression: 'DEFLATE',
        compressionOptions: { level: 9 }
    });
    const baseName = fileName.replace(/\.[^.]+$/, '');
    downloadBlob(blob, `${baseName}_translated.docx`);
}

// ============================================
// DEBUG EXPORT
// ============================================

export function exportDebugLog(fileName: string): void {
    const debug = (window as any).__exportDocxDebug;
    if (!debug) {
        alert('Chua co du lieu debug. Bam DOCX truoc roi bam Debug.');
        return;
    }

    const { sourceLines, translatedLines, paragraphMapTexts, stats } = debug;

    let report = `=== DOCX EXPORT DEBUG (PARAGRAPH-MAP BASED) ===\n`;
    report += `File: ${fileName}\n`;
    report += `Source lines: ${stats.sourceLineCount}\n`;
    report += `ParagraphMap (active): ${stats.paragraphMapCount}\n`;
    report += `ParagraphMap (fallback): ${stats.fallbackCount}\n`;
    report += `Replaced: ${stats.replacedCount}\n`;
    report += `Alignment mismatches (first 20): ${stats.mismatchCount}\n\n`;

    report += `=== MAPPING: ParagraphMap[i] = SourceLine[i] = TranslatedLine[i] ===\n`;
    const count = Math.min(sourceLines.length, paragraphMapTexts.length);
    for (let i = 0; i < count; i++) {
        const srcLine = sourceLines[i] || '';
        const paraText = paragraphMapTexts[i] || '';
        const traLine = translatedLines[i] || '';

        const srcNorm = srcLine.trim().replace(/\s+/g, ' ');
        const paraNorm = paraText.trim().replace(/\s+/g, ' ');
        const match = srcNorm.toLowerCase() === paraNorm.toLowerCase();
        const icon = match ? 'OK' : 'DIFF';

        report += `[${icon}] #${i}\n`;
        report += `  MAP:  ${paraText.substring(0, 100)}\n`;
        report += `  SRC:  ${srcLine.substring(0, 100)}\n`;
        report += `  TRA:  ${traLine.substring(0, 100)}\n\n`;
    }

    // Neu count khac nhau, log phan du
    if (sourceLines.length > paragraphMapTexts.length) {
        report += `\n=== SOURCE LINES THUA ===\n`;
        for (let i = paragraphMapTexts.length; i < sourceLines.length; i++) {
            report += `[EXTRA] #${i}: ${sourceLines[i].substring(0, 100)}\n`;
        }
    }
    if (paragraphMapTexts.length > sourceLines.length) {
        report += `\n=== PARAGRAPHMAP ENTRIES THUA ===\n`;
        for (let i = sourceLines.length; i < paragraphMapTexts.length; i++) {
            report += `[EXTRA] #${i}: ${paragraphMapTexts[i].substring(0, 100)}\n`;
        }
    }

    const blob = new Blob(['\ufeff' + report], { type: 'text/plain;charset=utf-8' });
    const baseName = fileName.replace(/\.[^.]+$/, '');
    downloadBlob(blob, `${baseName}_debug.txt`);
}

// ============================================
// DIAGNOSTIC EXPORTS - Tim root cause DOCX corrupt
// ============================================

/**
 * Test 1: Re-zip original DOCX khong sua gi.
 * Neu file nay MO DUOC -> van de o XML modifications.
 * Neu file nay KHONG MO DUOC -> van de o JSZip compression.
 */
export async function exportDocxNoChange(originalFile: ArrayBuffer, fileName: string): Promise<void> {
    if (!isValidArrayBuffer(originalFile)) {
        alert('File DOCX goc khong con trong bo nho (do reload trang).\nVui long upload lai file DOCX de export.');
        console.error('[Diagnostic] originalFile la', originalFile, '- khong phai ArrayBuffer hop le');
        return;
    }
    const zip = await JSZip.loadAsync(originalFile);
    const blob = await zip.generateAsync({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        compression: 'DEFLATE',
        compressionOptions: { level: 9 }
    });
    const baseName = fileName.replace(/\.[^.]+$/, '');
    downloadBlob(blob, `${baseName}_NO_CHANGE.docx`);
    console.log('[Diagnostic] Exported DOCX with NO changes - test if JSZip re-zip works');
}

/**
 * Test 2: Export modified document.xml rieng.
 * De kiem tra XML co valid khong bang cach paste vao XML validator.
 */
export async function exportModifiedXml(originalFile: ArrayBuffer, fileName: string): Promise<void> {
    const debug = (window as any).__exportDocxDebug;
    if (!debug?.modifiedXml) {
        alert('Chua co du lieu. Bam Export DOCX truoc.');
        return;
    }
    const blob = new Blob(['\ufeff' + debug.modifiedXml], { type: 'text/xml;charset=utf-8' });
    const baseName = fileName.replace(/\.[^.]+$/, '');
    downloadBlob(blob, `${baseName}_document.xml`);
    console.log('[Diagnostic] Exported modified document.xml for inspection');
}

/**
 * Test 3: Export ORIGINAL document.xml tu DOCX goc.
 * De so sanh voi modified XML -> tim chinh xac cho nao bi corrupt.
 */
export async function exportOriginalXml(originalFile: ArrayBuffer, fileName: string): Promise<void> {
    if (!isValidArrayBuffer(originalFile)) {
        alert('File DOCX goc khong con trong bo nho (do reload trang).\nVui long upload lai file DOCX de export.');
        return;
    }
    const zip = await JSZip.loadAsync(originalFile);
    const docXml = await zip.file('word/document.xml')?.async('string');
    if (!docXml) {
        alert('Khong tim thay word/document.xml trong file DOCX goc.');
        return;
    }
    const blob = new Blob(['\ufeff' + docXml], { type: 'text/xml;charset=utf-8' });
    const baseName = fileName.replace(/\.[^.]+$/, '');
    downloadBlob(blob, `${baseName}_ORIGINAL_document.xml`);
    console.log('[Diagnostic] Exported ORIGINAL document.xml for comparison');
}

// ============================================
// EXPORT PPTX
// ============================================

/**
 * Export translated PPTX by replacing text in slide XMLs.
 *
 * Strategy (similar to DOCX):
 *   1. Open original PPTX (ZIP)
 *   2. For each slide, use PptxSlideMapping to find paragraphs
 *   3. Map translated segments to paragraphs (1 line = 1 paragraph)
 *   4. Replace <a:t> content in each <a:p>, preserving formatting
 *   5. Re-zip and download
 */
export async function exportPptx(
    originalFile: ArrayBuffer,
    sourceSegments: string[],
    translatedSegments: string[],
    fileName: string,
    sourceText?: string,
    slideMappings?: PptxSlideMapping[]
): Promise<void> {
    if (!isValidArrayBuffer(originalFile)) {
        alert('File PPTX goc khong con trong bo nho (do reload trang).\nVui long upload lai file PPTX de export.');
        return;
    }

    if (!slideMappings || slideMappings.length === 0) {
        alert('Khong co slide mapping. Vui long upload lai file PPTX.');
        return;
    }

    const zip = await JSZip.loadAsync(originalFile);

    // Build a map: paragraph text -> translated text
    // sourceText lines (split by newline) map 1:1 to translatedSegments
    const sourceLines = (sourceText || '').replace(/\r\n/g, '\n').split(/\n+/).filter(l => l.trim());
    const translationMap = new Map<string, string>();
    for (let i = 0; i < sourceLines.length && i < translatedSegments.length; i++) {
        const src = sourceLines[i].trim();
        const trans = translatedSegments[i];
        if (src && trans && trans.trim()) {
            translationMap.set(src, trans);
        }
    }

    console.log(`[ExportPptx] ${translationMap.size} translation mappings, ${slideMappings.length} slides`);

    let totalReplaced = 0;

    for (const slideMap of slideMappings) {
        const slideFile = zip.file(slideMap.slidePath);
        if (!slideFile) continue;

        let slideXml = await slideFile.async('string');
        let replaced = 0;

        // Replace from end to start to maintain positions
        const parasToReplace = [...slideMap.paragraphs].sort((a, b) => b.xmlStart - a.xmlStart);

        for (const para of parasToReplace) {
            const translatedText = translationMap.get(para.text.trim());
            if (!translatedText) continue;

            const paraXml = slideXml.substring(para.xmlStart, para.xmlEnd);
            const newParaXml = replacePptxParagraphText(paraXml, translatedText);

            slideXml = slideXml.substring(0, para.xmlStart) + newParaXml + slideXml.substring(para.xmlEnd);
            replaced++;
        }

        if (replaced > 0) {
            zip.file(slideMap.slidePath, slideXml);
            totalReplaced += replaced;
        }
    }

    console.log(`[ExportPptx] Replaced ${totalReplaced} paragraphs in slides`);

    // Also replace text in SmartArt diagrams (data*.xml and drawing*.xml)
    const diagramFiles = Object.keys(zip.files)
        .filter(name => /^ppt\/diagrams\/(data|drawing)\d+\.xml$/.test(name));

    for (const diagPath of diagramFiles) {
        const diagFile = zip.file(diagPath);
        if (!diagFile) continue;

        let diagXml = await diagFile.async('string');
        let diagReplaced = 0;

        // Replace all <a:t>text</a:t> where trimmed text matches translationMap
        // Diagram text often has trailing spaces — match with optional \s* at end
        for (const [src, trans] of translationMap.entries()) {
            const escaped = src.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const re = new RegExp(`(<a:t(?:\\s[^>]*)?>)\\s*${escaped}\\s*(</a:t>)`, 'g');
            const before = diagXml;
            diagXml = diagXml.replace(re, `$1${escapeXml(trans)}$2`);
            if (diagXml !== before) diagReplaced++;
        }

        if (diagReplaced > 0) {
            zip.file(diagPath, diagXml);
            totalReplaced += diagReplaced;
            console.log(`[ExportPptx] Replaced ${diagReplaced} texts in ${diagPath}`);
        }
    }

    // Also replace chart labels (ppt/charts/chart*.xml)
    // Chart labels are in <c:v> tags
    const chartFiles = Object.keys(zip.files)
        .filter(name => /^ppt\/charts\/chart\d+\.xml$/.test(name));

    for (const chartPath of chartFiles) {
        const chartFile = zip.file(chartPath);
        if (!chartFile) continue;

        let chartXml = await chartFile.async('string');
        let chartReplaced = 0;

        for (const [src, trans] of translationMap.entries()) {
            const escaped = src.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const re = new RegExp(`(<c:v>)\\s*${escaped}\\s*(</c:v>)`, 'g');
            const before = chartXml;
            chartXml = chartXml.replace(re, `$1${escapeXml(trans)}$2`);
            if (chartXml !== before) chartReplaced++;
        }

        if (chartReplaced > 0) {
            zip.file(chartPath, chartXml);
            totalReplaced += chartReplaced;
            console.log(`[ExportPptx] Replaced ${chartReplaced} labels in ${chartPath}`);
        }
    }

    console.log(`[ExportPptx] Total replaced: ${totalReplaced}`);

    const blob = await zip.generateAsync({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        compression: 'DEFLATE',
        compressionOptions: { level: 9 }
    });
    const baseName = fileName.replace(/\.[^.]+$/, '');
    downloadBlob(blob, `${baseName}_translated.pptx`);
}

/**
 * Replace text content of a PPTX <a:p> paragraph.
 * Puts all new text into the first <a:t>, empties the rest.
 * Preserves all formatting (a:rPr, a:pPr, etc).
 *
 * IMPORTANT: Regex must match ONLY <a:t> or <a:t ...>, NOT <a:tbl>, <a:tcPr>, etc.
 * Use word boundary after "t": <a:t> or <a:t followed by space/>.
 */
function replacePptxParagraphText(paraXml: string, newText: string): string {
    let firstReplaced = false;
    // Match <a:t> or <a:t xml:space="preserve"> but NOT <a:tbl>, <a:tcPr>, <a:tblPr> etc.
    // Key: after "a:t" must be ">" or " " (space for attributes), not a letter.
    return paraXml.replace(/<a:t((?:\s[^>]*)?)>([^<]*)<\/a:t>/g, (_match, attrs, _content) => {
        if (!firstReplaced) {
            firstReplaced = true;
            return `<a:t${attrs || ''}>${escapeXml(newText)}</a:t>`;
        }
        // Subsequent <a:t> tags: empty them
        return `<a:t${attrs || ''}></a:t>`;
    });
}
